import { useState, useCallback, useMemo } from 'react'
import { useAzureConfig } from './useSecureConfig'

type ContainerName = 'necropsy' | 'logistic'
type ContainerType = { CONTAINER_NAME: string; TOKEN: string }
type FileList = { imageUri: string; imageName: string }

interface useBlobAzureUploadResult {
  uploadFiles: (files: FileList[]) => Promise<string[]>
  isUploading: boolean
  isConfigured: boolean
}

/**
 * Hook para subir archivos a Azure Blob Storage
 *
 * IMPORTANTE: Este hook usa EXCLUSIVAMENTE las credenciales del endpoint /me.
 * NO tiene fallback a app.json. Requiere que el usuario esté autenticado y la
 * configuración haya sido cargada.
 *
 * @param containerName - Nombre del contenedor ('necropsy' o 'logistic')
 */
export const useBlobAzure = ({
  containerName,
}: {
  containerName: ContainerName
}): useBlobAzureUploadResult => {
  const [isUploading, setIsUploading] = useState(false)

  // Obtener configuración EXCLUSIVAMENTE del endpoint /config/secure
  const secureAzureConfig = useAzureConfig()

  const isConfigured = !!secureAzureConfig

  // Configuración de Azure - SOLO desde endpoint
  const azureConfigSource = useMemo(() => {
    if (!secureAzureConfig) {
      console.error('Credenciales de Azure no disponibles. Usuario debe iniciar sesión.')
      return null
    }

    return {
      AZURE_STORAGE_ACCOUNT_NAME: secureAzureConfig.storageAccountName,
      NECROPSY: {
        CONTAINER_NAME: secureAzureConfig.containers.necropsy.name,
        TOKEN: secureAzureConfig.containers.necropsy.sasToken,
      },
      LOGISTIC: {
        CONTAINER_NAME: secureAzureConfig.containers.logistic.name,
        TOKEN: secureAzureConfig.containers.logistic.sasToken,
      },
    }
  }, [secureAzureConfig])

  const container = useMemo(() => {
    if (!azureConfigSource) return null

    const { NECROPSY, LOGISTIC } = azureConfigSource
    const CONTAINERS: Record<ContainerName, ContainerType> = {
      necropsy: NECROPSY,
      logistic: LOGISTIC,
    }
    return CONTAINERS[containerName]
  }, [azureConfigSource, containerName])

  const uploadFiles = useCallback(
    async (files: FileList[]): Promise<string[]> => {
      if (!azureConfigSource || !container) {
        console.error('No se puede subir archivos: configuración de Azure no disponible')
        throw new Error(
          'Configuración de Azure no disponible. Por favor, inicia sesión nuevamente.',
        )
      }

      const uploadedUrls: string[] = []
      setIsUploading(true)

      const checkIfBlobExists = async (sasUrl: string): Promise<boolean> => {
        try {
          const headResponse = await fetch(sasUrl, { method: 'HEAD' })
          return headResponse.ok
        } catch (error) {
          console.warn('Error verificando existencia de blob:', error)
          return false
        }
      }

      try {
        const { AZURE_STORAGE_ACCOUNT_NAME } = azureConfigSource

        for (const file of files) {
          try {
            const fileUrl = `https://${AZURE_STORAGE_ACCOUNT_NAME}.blob.core.windows.net/${container.CONTAINER_NAME}/${file.imageName}`
            const sasUrl = `${fileUrl}?${container.TOKEN}`

            const alreadyExists = await checkIfBlobExists(sasUrl)
            if (alreadyExists) {
              console.log(`Archivo ya existe: ${file.imageName}`)
              uploadedUrls.push(fileUrl)
              continue
            }

            const response = await fetch(file.imageUri)
            const blob = await response.blob()

            const uploadResponse = await fetch(sasUrl, {
              method: 'PUT',
              headers: {
                'x-ms-blob-type': 'BlockBlob',
                'Content-Type': blob.type || 'application/octet-stream',
              },
              body: blob,
            })

            if (!uploadResponse.ok) {
              const errorText = await uploadResponse.text()
              throw new Error(errorText || `Upload failed (${uploadResponse.status})`)
            }

            uploadedUrls.push(fileUrl)
          } catch (error) {
            console.error(`Error durante subida de ${file.imageName}:`, error)
          }
        }
      } finally {
        setIsUploading(false)
      }

      return uploadedUrls
    },
    [azureConfigSource, container],
  )

  return { uploadFiles, isUploading, isConfigured }
}
